import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CustomerHeader from '../components/CustomerHeader';
import CustomAlert from '../components/CustomAlert';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusTrackingPopup from '../components/StatusTrackingPopup';
import TrackingHistoryModal from '../components/TrackingHistoryModal';
import ticketService from '../services/ticketService';
import followUpContestService from '../services/followUpContestService';
import { FaClock, FaCheckCircle, FaExclamationCircle, FaTools, FaWrench, FaChevronRight, FaSpinner, FaHistory, FaTimes, FaCopy, FaSearch, FaCommentDots, FaExclamationTriangle } from 'react-icons/fa';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
`;

const ContentContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
`;

const Title = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.theme.primaryDark || '#c00000'};
  }

  &:active {
    transform: translateX(-2px);
  }
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  position: relative;
  overflow: visible;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex: 1;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.primary};
    border-radius: 4px;
  }
`;

const SearchIconButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.theme.primaryDark || '#c00000'};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SearchDropdown = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  width: 50%;
  min-width: 300px;
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  gap: 0.5rem;
  z-index: 1000;
  animation: slideDown 0.3s ease;
  border: 2px solid ${props => props.theme.primary};

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 0.9rem;
  outline: none;
  transition: all 0.3s;

  &:focus {
    border-color: ${props => props.theme.primary};
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const ClearSearchButton = styled.button`
  background: ${props => props.theme.surface};
  color: ${props => props.theme.textSecondary};
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.theme.border};
  }
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: none;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s;

  ${props => props.active ? `
    background: ${props.theme.primary};
    color: white;
  ` : `
    background: ${props.theme.surface};
    color: ${props.theme.textSecondary};

    &:hover {
      background: ${props.theme.border};
    }
  `}
`;

const TicketCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const TicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const TicketInfo = styled.div`
  flex: 1;
`;

const TicketNumber = styled.h3`
  color: ${props => props.theme.textPrimary};
  font-size: 1rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CountBadge = styled.span`
  background: ${props => props.theme.primary};
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    background: ${props => props.theme.primaryDark || '#c00000'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TicketDate = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.8rem;
  margin: 0.25rem 0 0;
`;

const TicketType = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
  margin: 0.5rem 0 0;
  font-weight: 500;
`;

const AssignedRole = styled.p`
  color: ${props => props.theme.primary};
  font-size: 0.8rem;
  margin: 0.25rem 0 0;
  font-weight: 500;
  background: rgba(244, 0, 0, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
`;

const LoginIdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const LoginIdLabel = styled.span`
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
  font-weight: 500;
`;

const LoginIdValue = styled.span`
  color: ${props => props.theme.textPrimary};
  font-size: 0.75rem;
  font-family: monospace;
  background: rgba(0, 0, 0, 0.05);
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
`;

const CopyLoginIdButton = styled.button`
  background: white;
  border: 1px solid ${props => props.theme.primary};
  color: ${props => props.theme.primary};
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.65rem;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(244, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TicketStatus = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  background: ${props => {
    switch(props.status) {
      case 'completed': return '#2e7d32';
      case 'in-progress': return '#1976d2';
      case 'pending': return '#f57c00';
      default: return '#6c757d';
    }
  }};
`;

const TicketDescription = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
  margin: 0.5rem 0 0;
  line-height: 1.4;
`;

const TicketFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${props => props.theme.border};
`;

const TicketAction = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${props => props.theme.primary};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateX(3px);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const StatusButton = styled.button`
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;

  &:hover {
    background: linear-gradient(135deg, #218838 0%, #1e7e34 100%);
    transform: translateY(-1px);
  }
`;

const TrackButton = styled.button`
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;

  &:hover {
    background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
    transform: translateY(-1px);
  }
`;

const SurveyButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.theme.primaryDark};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  background: white;
  border-radius: 12px;
  margin-top: 2rem;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  color: ${props => props.theme.border};
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 1rem;
`;

const DuplicatesModal = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 1rem;
`;

const DuplicatesContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const DuplicatesHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 2px solid ${props => props.theme.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const DuplicatesTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.primary};
`;

const DuplicatesCloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(244, 0, 0, 0.1);
  }
`;

const DuplicatesBody = styled.div`
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
`;

const DuplicateItem = styled.div`
  background: ${props => props.theme.surface || '#f5f5f5'};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.theme.border};

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const DuplicateItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const DuplicateItemTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  font-size: 0.9rem;
`;

const DuplicateItemDate = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const DuplicateItemType = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const FollowUpButton = styled.button`
  background: linear-gradient(135deg, #f57c00 0%, #e65100 100%);
  color: white;
  border: none;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &:hover {
    background: linear-gradient(135deg, #e65100 0%, #bf360c 100%);
    transform: translateY(-1px);
  }
`;

const ContestButton = styled.button`
  background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%);
  color: white;
  border: none;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &:hover {
    background: linear-gradient(135deg, #b71c1c 0%, #880e4f 100%);
    transform: translateY(-1px);
  }
`;

const DialogOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1002;
  padding: 1rem;
`;

const DialogContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const DialogHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 2px solid ${props => props.theme.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DialogTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.primary};
`;

const DialogCloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const DialogBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const DialogDescription = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9rem;
  margin: 0 0 1rem;
  line-height: 1.5;
`;

const DialogTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.3s;

  &:focus {
    border-color: ${props => props.theme.primary};
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const DialogFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const DialogCancelButton = styled.button`
  background: ${props => props.theme.surface};
  color: ${props => props.theme.textSecondary};
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.border};
  }
`;

const DialogSubmitButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? '#ccc' : props.theme.primary};
  color: white;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.disabled ? '#ccc' : (props.theme.primaryDark || '#c00000')};
  }
`;

const TicketsHistory: React.FC = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('open');
  const [isLoading, setIsLoading] = useState(true);
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = React.useRef<HTMLDivElement>(null);
  const [statusTrackingPopup, setStatusTrackingPopup] = useState<{
    isVisible: boolean;
    formName: string;
    currentStatus: string;
    ticketNumber: string;
    assignedRole?: string;
  }>({ isVisible: false, formName: '', currentStatus: '', ticketNumber: '' });
  const [trackingHistoryModal, setTrackingHistoryModal] = useState<{
    isVisible: boolean;
    recordUid: string;
    ticketNumber: string;
  }>({ isVisible: false, recordUid: '', ticketNumber: '' });
  const [duplicateTicketsModal, setDuplicateTicketsModal] = useState<{
    isVisible: boolean;
    tickets: any[];
    ticketNumber: string;
  }>({ isVisible: false, tickets: [], ticketNumber: '' });

  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    onConfirm?: () => void;
  }>({ isVisible: false, message: '' });

  const [copiedLoginId, setCopiedLoginId] = useState<string | null>(null);

  // Follow-Up & Contest state
  const [followUpContestDialog, setFollowUpContestDialog] = useState<{
    isVisible: boolean;
    type: 'FollowUp' | 'Contest';
    ticket: any;
  }>({ isVisible: false, type: 'FollowUp', ticket: null });
  const [followUpContestMessage, setFollowUpContestMessage] = useState('');
  const [followUpContestSubmitting, setFollowUpContestSubmitting] = useState(false);
  const [formGridConfigs, setFormGridConfigs] = useState<Record<string, any>>({});

  const copyLoginId = (loginId: string, ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(loginId);
    setCopiedLoginId(ticketId);
    setTimeout(() => setCopiedLoginId(null), 2000);
  };

  // Helper functions - defined before use
  const getCategoryFromFormName = (formName: string): string => {
    const name = formName?.toLowerCase() || '';
    if (name.includes('service') || name.includes('maintenance')) return 'Service';
    if (name.includes('installation') || name.includes('install')) return 'Installation';
    if (name.includes('removal') || name.includes('pullout')) return 'Removal';
    if (name.includes('relocation') || name.includes('relocate')) return 'Relocation';
    return 'Service';
  };

  const mapStatusToTicketStatus = (status: string): 'completed' | 'pending' | 'in-progress' => {
    const s = status?.toLowerCase() || '';
    if (s.includes('completed') || s.includes('closed') || s.includes('done')) return 'completed';
    if (s.includes('progress') || s.includes('processing') || s.includes('assigned')) return 'in-progress';
    return 'pending';
  };

  const isTicketClosed = (status: string): boolean => {
    const s = status?.toLowerCase() || '';
    return s.includes('completed') || s.includes('closed') || s.includes('done') || s.includes('finished');
  };

  // Load tickets on component mount and when filter changes
  useEffect(() => {
    loadTickets();
  }, [activeFilter]);

  // Click outside to close search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearch(false);
      }
    };

    if (showSearch) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearch]);

  const loadTickets = async () => {
    setIsLoading(true);
    try {
      const tabStatus = activeFilter === 'open' ? 'Open' : 'Closed';
      const response = await ticketService.getTickets(undefined, tabStatus);

      if (response.success && response.tickets) {
        setTickets(response.tickets);
      } else {
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Error',
          message: 'Failed to load tickets. Please try again later.'
        });
      }
    } catch (error: any) {
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to load tickets. Please try again later.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load FormGridConfiguration for unique form names to determine follow-up/contest eligibility
  useEffect(() => {
    if (tickets.length === 0) return;
    const uniqueFormNames = Array.from(new Set(tickets.map(t => t.formName || t.form_name).filter(Boolean)));
    uniqueFormNames.forEach(async (formName) => {
      if (formGridConfigs[formName]) return;
      try {
        const res = await followUpContestService.getFormGridConfiguration(formName);
        const configData = res?.Data?.FormsGridConfiguration || res?.data?.formsGridConfiguration;
        if (configData) {
          setFormGridConfigs(prev => ({ ...prev, [formName]: {
            IsFollowUpAllowed: configData.IsFollowUpAllowed ?? configData.isFollowUpAllowed ?? false,
            IsContestAllowed: configData.IsContestAllowed ?? configData.isContestAllowed ?? false,
            TakeCDEAFrom: configData.TakeCDEAFrom || configData.takeCDEAFrom || '',
            CDEAColumnName: configData.CDEAColumnName || configData.cdeaColumnName || '',
          }}));
        }
      } catch (err: any) {
        console.error(`[FollowUp/Contest] Error fetching config for ${formName}:`, err.message || err);
      }
    });
  }, [tickets]);

  const canShowFollowUp = (ticket: any): boolean => {
    const formName = ticket.type || '';
    const config = formGridConfigs[formName];
    if (!config?.IsFollowUpAllowed) return false;
    // Don't show for closed/completed tickets
    if (ticket.isClosed) return false;
    // Use first history time (original creation from service_task_history)
    // Fall back to created date if history time not available
    const checkTime = ticket.firstHistoryTime || ticket.date;
    if (!checkTime) return false;
    const checkDate = new Date(checkTime);
    const now = new Date();
    const diffDays = (now.getTime() - checkDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays > 3;
  };

  const canShowContest = (ticket: any): boolean => {
    const formName = ticket.type || '';
    const config = formGridConfigs[formName];
    if (!config?.IsContestAllowed) return false;
    // Only show for closed/completed tickets
    if (!ticket.isClosed) return false;
    // Only show within 30 days of modified time
    const modifiedTime = ticket.serverModifiedTime || ticket.date;
    if (!modifiedTime) return false;
    const modified = new Date(modifiedTime);
    const now = new Date();
    const diffDays = (now.getTime() - modified.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 30;
  };

  const handleFollowUp = async (ticket: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const formUID = ticket.formUid || ticket.id;
    try {
      const res = await followUpContestService.checkFollowUpExists(formUID);
      const existsData = res?.Data || res?.data;
      if (existsData?.Exists || existsData?.exists) {
        const existingUID = existsData?.UID || existsData?.uid || '';
        navigate(`/followup-chat?uid=${existingUID}&type=FollowUp`);
        return;
      }
    } catch (err) {
      // If check fails, allow them to create a new one
    }
    setFollowUpContestDialog({ isVisible: true, type: 'FollowUp', ticket });
    setFollowUpContestMessage('');
  };

  const handleContest = async (ticket: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const formUID = ticket.formUid || ticket.id;
    try {
      const res = await followUpContestService.checkContestExists(formUID);
      const existsData = res?.Data || res?.data;
      if (existsData?.Exists || existsData?.exists) {
        const existingUID = existsData?.UID || existsData?.uid || '';
        navigate(`/contest-chat?uid=${existingUID}&type=Contest`);
        return;
      }
    } catch (err) {
      // If check fails, allow them to create a new one
    }
    setFollowUpContestDialog({ isVisible: true, type: 'Contest', ticket });
    setFollowUpContestMessage('');
  };

  const submitFollowUpContest = async () => {
    if (!followUpContestMessage.trim()) {
      setAlert({ isVisible: true, type: 'warning', title: 'Warning', message: 'Please enter a message.' });
      return;
    }

    const ticket = followUpContestDialog.ticket;
    if (!ticket) return;

    setFollowUpContestSubmitting(true);
    const formName = ticket.type || '';
    const config = formGridConfigs[formName] || {};

    const request = {
      FormName: formName,
      FormUID: ticket.formUid || ticket.id || '',
      FormNumber: (ticket.number || '').replace('#', ''),
      Message: followUpContestMessage,
      TakeCDEAFrom: config.TakeCDEAFrom || '',
      CDEAColumnName: config.CDEAColumnName || '',
    };

    try {
      const submitFn = followUpContestDialog.type === 'FollowUp'
        ? followUpContestService.submitFollowUp(request)
        : followUpContestService.submitContest(request);

      const res = await submitFn;
      setFollowUpContestSubmitting(false);
      setFollowUpContestDialog({ isVisible: false, type: 'FollowUp', ticket: null });

      const resData = res?.Data || res?.data;
      const statusOk = res?.Status?.Type === 1 || res?.status?.type === 1 || resData;
      if (statusOk) {
        const chatUID = resData?.FollowUpUID || resData?.followUpUID ||
                        resData?.ContestUID || resData?.contestUID || '';
        if (chatUID) {
          const chatType = followUpContestDialog.type;
          const route = chatType === 'FollowUp' ? '/followup-chat' : '/contest-chat';
          navigate(`${route}?uid=${chatUID}&type=${chatType}`);
        } else {
          setAlert({
            isVisible: true,
            type: 'success',
            title: 'Success',
            message: `${followUpContestDialog.type === 'FollowUp' ? 'Follow-up' : 'Contest'} submitted successfully.`
          });
        }
      } else {
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Error',
          message: res?.Status?.Msg || res?.status?.msg || 'Failed to submit. Please try again.'
        });
      }
    } catch (err: any) {
      setFollowUpContestSubmitting(false);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Error',
        message: err.message || 'Failed to submit. Please try again.'
      });
    }
  };

  // Transform tickets for display
  const allDisplayTickets = tickets.map(ticket => {
    const formName = ticket.formName || ticket.form_name || 'Service Request';
    const ticketStatus = ticket.status || ticket.form_status || 'Unknown';
    const assignedRole = ticket.assignedRole || 'Not Assigned';
    const assignedTo = ticket.assigned_to || ticket.assignedTo || '';

    return {
      id: ticket.uid,
      formUid: ticket.form_uid, // CRITICAL: Use form_uid for history API
      number: `#${ticket.form_number || ticket.uid?.substring(0, 8) || 'N/A'}`,
      date: new Date(ticket.createdTime || ticket.created_time).toISOString().split('T')[0],
      type: formName,
      category: getCategoryFromFormName(formName),
      description: `${formName} - Status: ${ticketStatus}`,
      assignedRole: assignedRole,
      assignedTo: assignedTo, // NEW: Store assigned login ID
      cdeType: 'CDE Equipment',
      status: mapStatusToTicketStatus(ticketStatus),
      actualStatus: ticketStatus, // Keep actual status string for status tracking
      isClosed: isTicketClosed(ticketStatus),
      surveyAvailable: ticketStatus?.toLowerCase() === 'completed',
      serverModifiedTime: ticket.server_modified_time || ticket.serverModifiedTime || ticket.modified_time || ticket.modifiedTime || '',
      firstHistoryTime: ticket.first_history_time || ticket.firstHistoryTime || '',
      originalTicket: ticket
    };
  });

  // Group tickets by ticket number and keep only first occurrence with count
  const ticketNumberMap = new Map<string, { ticket: any; count: number; allTickets: any[] }>();

  allDisplayTickets.forEach(ticket => {
    const existing = ticketNumberMap.get(ticket.number);
    if (existing) {
      existing.count++;
      existing.allTickets.push(ticket);
    } else {
      ticketNumberMap.set(ticket.number, {
        ticket: ticket,
        count: 1,
        allTickets: [ticket]
      });
    }
  });

  // Convert map to array with count information
  const displayTickets = Array.from(ticketNumberMap.values()).map(({ ticket, count, allTickets }) => ({
    ...ticket,
    duplicateCount: count,
    allDuplicates: allTickets
  }));

  // Apply frontend filtering as well to ensure correct filtering
  const filteredTickets = displayTickets.filter(ticket => {
    // Filter by open/closed status
    let matchesFilter = true;
    if (activeFilter === 'open') {
      matchesFilter = !ticket.isClosed;
    } else if (activeFilter === 'closed') {
      matchesFilter = ticket.isClosed;
    }

    // Filter by search query
    let matchesSearch = true;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      matchesSearch =
        ticket.number?.toLowerCase().includes(query) ||
        ticket.type?.toLowerCase().includes(query) ||
        ticket.actualStatus?.toLowerCase().includes(query) ||
        ticket.date?.toLowerCase().includes(query);
    }

    return matchesFilter && matchesSearch;
  });

  const handleTicketClick = (ticketId: string) => {
    const ticket = displayTickets.find(t => t.id === ticketId);
    if (!ticket) return;

    // Open tracking history modal to show complete timeline
    // CRITICAL: Use formUid for the history API, not the ticket uid
    setTrackingHistoryModal({
      isVisible: true,
      recordUid: ticket.formUid || ticket.id, // Use form_uid from API response
      ticketNumber: ticket.number
    });
  };

  const handleTrackStatus = (ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const ticket = displayTickets.find(t => t.id === ticketId);
    if (!ticket) return;

    // Show status tracking popup with current workflow status (using first ticket's data)
    setStatusTrackingPopup({
      isVisible: true,
      formName: ticket.type,
      currentStatus: ticket.actualStatus || ticket.status,
      ticketNumber: ticket.number,
      assignedRole: ticket.assignedRole
    });
  };

  const handleSurvey = (ticketId: string) => {
    navigate(`/survey/${ticketId}`);
  };

  const handleShowDuplicates = (ticket: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (ticket.duplicateCount > 1) {
      setDuplicateTicketsModal({
        isVisible: true,
        tickets: ticket.allDuplicates || [],
        ticketNumber: ticket.number
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'completed':
        return React.createElement(FaCheckCircle as any, { color: "#2e7d32" });
      case 'pending':
        return React.createElement(FaClock as any, { color: "#f57c00" });
      case 'in-progress':
        return React.createElement(FaTools as any, { color: "#1976d2" });
      default:
        return React.createElement(FaExclamationCircle as any, {});
    }
  };

  const getCategoryIcon = (category: string) => {
    switch(category) {
      case 'Service':
        return React.createElement(FaWrench as any, {});
      case 'Installation':
        return React.createElement(FaTools as any, {});
      default:
        return React.createElement(FaTools as any, {});
    }
  };

  const filters = [
    { id: 'open', label: 'Open Tickets' },
    { id: 'closed', label: 'Closed Tickets' }
  ];

  return (
    <PageContainer>
      <CustomerHeader skipAuthCheck={true} />
      <ContentContainer>
        <BackButton onClick={() => navigate('/dashboard')}>
          ← Back
        </BackButton>
        <Title>Tickets History</Title>

        <FilterSection ref={searchRef}>
          <FilterButtonsContainer>
            {filters.map(filter => (
              <FilterButton
                key={filter.id}
                active={activeFilter === filter.id}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
              </FilterButton>
            ))}
          </FilterButtonsContainer>
          <SearchIconButton onClick={() => setShowSearch(!showSearch)}>
            {React.createElement(FaSearch as any, { size: 16 })}
          </SearchIconButton>
          <SearchDropdown isVisible={showSearch}>
            <SearchInput
              type="text"
              placeholder="Search by ticket number, type, status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            <ClearSearchButton onClick={() => {
              setSearchQuery('');
              setShowSearch(false);
            }}>
              Clear
            </ClearSearchButton>
          </SearchDropdown>
        </FilterSection>

        {isLoading ? (
          <LoadingSpinner isVisible={true} />
        ) : filteredTickets.length > 0 ? (
          filteredTickets.map(ticket => (
            <TicketCard key={ticket.id} onClick={() => handleTicketClick(ticket.id)}>
              <TicketHeader>
                <TicketInfo>
                  <TicketNumber>
                    {getCategoryIcon(ticket.category)}
                    {ticket.number}
                  </TicketNumber>
                  <TicketDate>{ticket.date}</TicketDate>
                  <TicketType>{ticket.type}</TicketType>
                </TicketInfo>
                <TicketStatus status={ticket.status}>
                  {getStatusIcon(ticket.status)}
                  {ticket.actualStatus}
                </TicketStatus>
              </TicketHeader>

              <TicketFooter>
                <ButtonGroup>
                  <StatusButton onClick={(e: React.MouseEvent) => handleTrackStatus(ticket.id, e)}>
                    Track Status
                  </StatusButton>
                  {ticket.surveyAvailable && (
                    <SurveyButton onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleSurvey(ticket.id);
                    }}>
                      Take Survey
                    </SurveyButton>
                  )}
                  {canShowFollowUp(ticket) && (
                    <FollowUpButton onClick={(e: React.MouseEvent) => handleFollowUp(ticket, e)}>
                      {React.createElement(FaCommentDots as any, { size: 12 })}
                      Follow-Up
                    </FollowUpButton>
                  )}
                  {canShowContest(ticket) && (
                    <ContestButton onClick={(e: React.MouseEvent) => handleContest(ticket, e)}>
                      {React.createElement(FaExclamationTriangle as any, { size: 12 })}
                      Contest
                    </ContestButton>
                  )}
                </ButtonGroup>
              </TicketFooter>
            </TicketCard>
          ))
        ) : (
          <EmptyState>
            <EmptyIcon>
              {React.createElement(FaTools as any, {})}
            </EmptyIcon>
            <EmptyText>
              {activeFilter === 'open'
                ? 'No open tickets found. Your current tickets will appear here.'
                : 'No closed tickets found. Your completed tickets will appear here.'
              }
            </EmptyText>
          </EmptyState>
        )}

        <CustomAlert
          isVisible={alert.isVisible}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert({ isVisible: false, message: '' })}
          onConfirm={alert.onConfirm}
        />

        <StatusTrackingPopup
          isVisible={statusTrackingPopup.isVisible}
          formName={statusTrackingPopup.formName}
          currentStatus={statusTrackingPopup.currentStatus}
          ticketNumber={statusTrackingPopup.ticketNumber}
          assignedRole={statusTrackingPopup.assignedRole}
          onClose={() => setStatusTrackingPopup({ isVisible: false, formName: '', currentStatus: '', ticketNumber: '' })}
        />

        <TrackingHistoryModal
          isOpen={trackingHistoryModal.isVisible}
          onClose={() => setTrackingHistoryModal({ isVisible: false, recordUid: '', ticketNumber: '' })}
          recordUid={trackingHistoryModal.recordUid}
          ticketNumber={trackingHistoryModal.ticketNumber}
        />

        <DuplicatesModal
          isVisible={duplicateTicketsModal.isVisible}
          onClick={() => setDuplicateTicketsModal({ isVisible: false, tickets: [], ticketNumber: '' })}
        >
          <DuplicatesContainer onClick={(e) => e.stopPropagation()}>
            <DuplicatesHeader>
              <DuplicatesTitle>
                Duplicate Tickets - {duplicateTicketsModal.ticketNumber}
              </DuplicatesTitle>
              <DuplicatesCloseButton onClick={() => setDuplicateTicketsModal({ isVisible: false, tickets: [], ticketNumber: '' })}>
                {React.createElement(FaTimes as any, {})}
              </DuplicatesCloseButton>
            </DuplicatesHeader>
            <DuplicatesBody>
              {duplicateTicketsModal.tickets.map((ticket, index) => (
                <DuplicateItem
                  key={ticket.id}
                  onClick={() => {
                    setDuplicateTicketsModal({ isVisible: false, tickets: [], ticketNumber: '' });
                    handleTicketClick(ticket.id);
                  }}
                >
                  <DuplicateItemHeader>
                    <div>
                      <DuplicateItemTitle>
                        {getCategoryIcon(ticket.category)} Instance #{index + 1}
                      </DuplicateItemTitle>
                      <DuplicateItemDate>{ticket.date}</DuplicateItemDate>
                    </div>
                    <TicketStatus status={ticket.status}>
                      {getStatusIcon(ticket.status)}
                      {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                    </TicketStatus>
                  </DuplicateItemHeader>
                  <DuplicateItemType>{ticket.type}</DuplicateItemType>
                </DuplicateItem>
              ))}
            </DuplicatesBody>
          </DuplicatesContainer>
        </DuplicatesModal>
        {/* Follow-Up / Contest Dialog */}
        <DialogOverlay
          isVisible={followUpContestDialog.isVisible}
          onClick={() => setFollowUpContestDialog({ isVisible: false, type: 'FollowUp', ticket: null })}
        >
          <DialogContainer onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>
                {followUpContestDialog.type === 'FollowUp' ? 'Submit Follow-Up' : 'Submit Contest'}
              </DialogTitle>
              <DialogCloseButton onClick={() => setFollowUpContestDialog({ isVisible: false, type: 'FollowUp', ticket: null })}>
                {React.createElement(FaTimes as any, {})}
              </DialogCloseButton>
            </DialogHeader>
            <DialogBody>
              <DialogDescription>
                {followUpContestDialog.type === 'FollowUp'
                  ? 'Follow up on this service request if you need additional assistance or have queries.'
                  : 'Contest the completed service if the issue has reoccurred. Please describe the issue.'
                }
              </DialogDescription>
              <DialogTextarea
                placeholder="Enter your message..."
                value={followUpContestMessage}
                onChange={(e) => setFollowUpContestMessage(e.target.value)}
              />
            </DialogBody>
            <DialogFooter>
              <DialogCancelButton onClick={() => setFollowUpContestDialog({ isVisible: false, type: 'FollowUp', ticket: null })}>
                Cancel
              </DialogCancelButton>
              <DialogSubmitButton
                disabled={followUpContestSubmitting || !followUpContestMessage.trim()}
                onClick={submitFollowUpContest}
              >
                {followUpContestSubmitting && React.createElement(FaSpinner as any, { className: 'spin', size: 14 })}
                {followUpContestDialog.type === 'FollowUp' ? 'Submit Follow-Up' : 'Submit Contest'}
              </DialogSubmitButton>
            </DialogFooter>
          </DialogContainer>
        </DialogOverlay>
      </ContentContainer>
    </PageContainer>
  );
};

export default TicketsHistory;